import type { ReactElement } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { PermissionRoute } from "../components/auth/PermissionRoute";
import { HomePage } from "../pages/HomePage";
import { ContractIQPage } from "../pages/ContractIQPage";
import { ContractListPage } from "../pages/ContractListPage";
import { ReviewPage } from "../pages/ReviewPage";
import { ContractSubmitForReviewPage } from "../pages/ContractSubmitForReviewPage";
import { SuggestionReviewDetailPage } from "../pages/SuggestionReviewDetailPage";
import { TemplateLibraryPage } from "../pages/TemplateLibraryPage";
import { ReviewTemplatePage } from "../pages/ReviewTemplatePage";
import { SendForReviewPage } from "../pages/SendForReviewPage";
import { UploadAndReviewPage } from "../pages/UploadAndReviewPage";
import { PrizeLogicHubPage } from "../pages/PrizeLogicHubPage";
import { PrizeLogicLibraryPage } from "../pages/PrizeLogicLibraryPage";
import { PrizeLogicListingPage } from "../pages/PrizeLogicListingPage";
import { PrizeLogicGeneratedDocumentReviewPage } from "../pages/PrizeLogicGeneratedDocumentReviewPage";
import { CreateGameRuleTemplatePage } from "../pages/CreateGameRuleTemplatePage";
import { GameRuleGenerateLandingPage } from "../pages/GameRuleGenerateLandingPage";
import { GameRuleGenerateTemplatesPage } from "../pages/GameRuleGenerateTemplatesPage";
import { GameRuleGenerateFlowPage } from "../pages/GameRuleGenerateFlowPage";
import { GameRuleGeneratePage } from "../pages/GameRuleGeneratePage";
import { LoginPage } from "../pages/LoginPage";
import { OktaCallbackPage } from "../pages/OktaCallbackPage";
import { OktaDialogStartPage } from "../pages/OktaDialogStartPage";
import { isAuthenticated } from "../services/authService";

const RequireAuth = ({ children }: { children: ReactElement }) =>
  isAuthenticated() ? children : <Navigate to="/login" replace />;

const RequireContractsModule = ({ children }: { children: ReactElement }) => (
  <RequireAuth>
    <PermissionRoute requireContractsModule>{children}</PermissionRoute>
  </RequireAuth>
);

const RequirePrizeRulesModule = ({ children }: { children: ReactElement }) => (
  <RequireAuth>
    <PermissionRoute requirePrizeRulesModule>{children}</PermissionRoute>
  </RequireAuth>
);

export const AppRouter = () => (
  <Routes>
    <Route path="/login" element={<LoginPage />} />
    <Route path="/okta-dialog-start" element={<OktaDialogStartPage />} />
    <Route path="/okta-callback" element={<OktaCallbackPage />} />
    <Route path="/" element={<RequireAuth><HomePage /></RequireAuth>} />
    <Route path="/index.html" element={<Navigate to="/" replace />} />
    <Route path="/contractiq" element={<RequireContractsModule><ContractIQPage /></RequireContractsModule>} />
    <Route path="/contractiq/contracts" element={<RequireContractsModule><ContractListPage /></RequireContractsModule>} />
    <Route path="/contractiq/upload" element={<RequireContractsModule><UploadAndReviewPage /></RequireContractsModule>} />
    <Route path="/contractiq/contracts/:id/review" element={<RequireContractsModule><ReviewPage /></RequireContractsModule>} />
    <Route
      path="/contractiq/contracts/:id/review/submit"
      element={<RequireContractsModule><ContractSubmitForReviewPage /></RequireContractsModule>}
    />
    <Route
      path="/contractiq/contracts/:id/review/suggestion/:suggestionItemId"
      element={<RequireContractsModule><SuggestionReviewDetailPage /></RequireContractsModule>}
    />
    <Route path="/contractiq/templates" element={<RequireContractsModule><TemplateLibraryPage /></RequireContractsModule>} />
    <Route path="/contractiq/templates/:id/review" element={<RequireContractsModule><ReviewTemplatePage /></RequireContractsModule>} />
    <Route path="/contractiq/templates/:id/send" element={<RequireContractsModule><SendForReviewPage /></RequireContractsModule>} />
    <Route path="/prizelogic" element={<RequirePrizeRulesModule><PrizeLogicHubPage /></RequirePrizeRulesModule>} />
    <Route path="/prizelogic/listing" element={<RequirePrizeRulesModule><PrizeLogicListingPage /></RequirePrizeRulesModule>} />
    <Route path="/prizelogic/library" element={<RequirePrizeRulesModule><PrizeLogicLibraryPage /></RequirePrizeRulesModule>} />
    <Route path="/prizelogic/generate-document" element={<RequirePrizeRulesModule><GameRuleGenerateLandingPage /></RequirePrizeRulesModule>} />
    <Route path="/prizelogic/generate-document/templates" element={<RequirePrizeRulesModule><GameRuleGenerateTemplatesPage /></RequirePrizeRulesModule>} />
    <Route path="/prizelogic/generate-document/wizard" element={<RequirePrizeRulesModule><GameRuleGenerateFlowPage /></RequirePrizeRulesModule>} />
    <Route
      path="/prizelogic/generated-documents"
      element={<RequirePrizeRulesModule><Navigate to="/prizelogic/listing" replace /></RequirePrizeRulesModule>}
    />
    <Route
      path="/prizelogic/generated-documents/:documentId/review"
      element={
        <RequirePrizeRulesModule>
          <PrizeLogicGeneratedDocumentReviewPage />
        </RequirePrizeRulesModule>
      }
    />
    <Route
      path="/prizelogic/generate/:templateId/:sessionId"
      element={<RequirePrizeRulesModule><GameRuleGeneratePage /></RequirePrizeRulesModule>}
    />
    <Route path="/prizelogic/create-template" element={<RequirePrizeRulesModule><CreateGameRuleTemplatePage /></RequirePrizeRulesModule>} />
    <Route
      path="/prizelogic/templates/:templateId/edit"
      element={<RequirePrizeRulesModule><CreateGameRuleTemplatePage /></RequirePrizeRulesModule>}
    />
    <Route path="*" element={<Navigate to={isAuthenticated() ? "/" : "/login"} replace />} />
  </Routes>
);
